import cx from 'classnames';
import { ControlLabel, Modal } from 'react-bootstrap';
import type { VoidFunctionComponent } from 'react';

import Locator from '../../../../shared/Locator';
import useBlockPackageVersions from '../../../hooks/api/packages/useBlockPackageVersions';
import Button from '../../generic/Button/Button';
import ButtonGroup from '../../generic/Button/ButtonGroup';
import Flex from '../../generic/Flex';
import Icon, { icons } from '../../generic/Icon';
import Select from '../../generic/Select/Select';
import useQualityPolicySelector from '../hooks/useQualityPolicySelector';
import type { FetchPackageMetadataResponse, PackageDetails } from '../../../../shared/organizationPackages/response';

import css from './BlockPackageModal.module.less';

const getVersion = (packageRevision: string) => Locator.parse(packageRevision).revision ?? packageRevision;

type Props = {
  isDisplayed: boolean;
  onHide: () => void;
  packageLocator: string;
  packageMetadata: FetchPackageMetadataResponse | undefined;
  isBlockingAllVersions?: boolean;
  selectedVersions: PackageDetails[];
  onDeselectAll: () => void;
  onToggleVersion: (packageRevision: string) => void;
};

/**
 * BlockPackageModal is a modal which displays the list of versions
 * which should be ignored, along with a quality policy multi-select
 * where users can select which policies the block rule should apply to.
 */
const BlockPackageModal: VoidFunctionComponent<Props> = ({
  isDisplayed,
  onHide,
  packageLocator,
  packageMetadata,
  isBlockingAllVersions,
  selectedVersions,
  onDeselectAll,
  onToggleVersion,
}) => {
  const { mutate, isLoading } = useBlockPackageVersions();

  const qualityPolicySelector = useQualityPolicySelector();

  // Keep the modal open until the mutation succeeds so a failed request can be retried.
  const onBlockPackageVersions = () => {
    mutate({
      packageLocator,
      policyIds: qualityPolicySelector.selectedPolicies.map(policy => policy.value),
      versions: isBlockingAllVersions
        ? 'ALL'
        : selectedVersions.map(({ packageRevision }) => getVersion(packageRevision)),
    }, {
      onSuccess: () => {
        onDeselectAll();
        onHide();
      },
    });
  };

  //  Note: this would never happen, as the button to open this modal can't be clicked
  //  until the packageMetadata has loaded. This just ensures that TS is happy for the rest of this
  if (!packageMetadata) {
    return null;
  }

  return (
    <Modal
      show={isDisplayed}
      onHide={onHide}
    >
      <Modal.Header className={css.header} closeButton>
        <h1 className={css.heading}>
          Block package
        </h1>
        <p className={css.subheading}>
          Create a new rule blocking this package.
        </p>
      </Modal.Header>
      <Modal.Body className={css.body}>
        <>
            <div className="margin-bottom-lg">
              <div className={css.label}>
                Package
              </div>
              <div className={css.packageName}>
                {packageMetadata.name}
              </div>
              <div className={css.packageLocator}>
                {packageMetadata.locator}
              </div>
            </div>
            <div className="margin-bottom-lg">
              <div className={css.label}>
                Versions
              </div>
              <Flex justify="flex-start" wrap="wrap" gap="2px">
                {isBlockingAllVersions ? (
                  <div className={css.allVersionsText}>
                    All versions
                  </div>
                ) : selectedVersions.map(({ packageRevision }) => (
                  <span className={cx(css.chip, {
                    [css.chipSolo]: selectedVersions.length === 1,
                  })} key={packageRevision}>
                    {getVersion(packageRevision)}
                    {selectedVersions.length > 1 && (
                      <button
                        type="button"
                        className={css.chipIconButton}
                        aria-label={`Remove version ${getVersion(packageRevision)}`}
                        onClick={() => selectedVersions.length > 0 && onToggleVersion(packageRevision)}
                        disabled={isLoading || selectedVersions.length === 1}
                      >
                        <Icon icon={icons.times} />
                      </button>
                    )}
                  </span>
                ))}
              </Flex>
            </div>
            <div className="margin-bottom-lg">
              <div className="multiselect-filter-group">
                <ControlLabel className="multiselect-filter-group-title">Policies</ControlLabel>
                <Select
                  isMulti
                  isClearable
                  id="Policies"
                  className="multiselect-filter-group-select"
                  placeholder={qualityPolicySelector.isLoading ? 'Loading...' : 'Select one or more...'}
                  isDisabled={isLoading || qualityPolicySelector.isLoading}
                  value={qualityPolicySelector.selectedPolicies}
                  options={qualityPolicySelector.policies}
                  onChange={qualityPolicySelector.setSelectedPolicies}
                />
              </div>
            </div>
          </>
      </Modal.Body>
      <Modal.Footer bsClass={css.footer}>
        <ButtonGroup>
          <Button
            type="secondary"
            size="large"
            onClick={onHide}
            isDisabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="error"
            size="large"
            onClick={onBlockPackageVersions}
            isDisabled={isLoading || qualityPolicySelector.selectedPolicies.length === 0}
          >
            {isLoading ? 'Blocking package...' : 'Block package'}
          </Button>
        </ButtonGroup>
      </Modal.Footer>
    </Modal>
  );
};

export default BlockPackageModal;
